import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { usePaymentHistory } from "../hooks/usePaymentHistory";
import { StudentPOStatusShort } from "../types";

const cellSx = {
  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
  whiteSpace: "nowrap" as const,
  px: { xs: 0.75, sm: 2 },
};

export const PaymentsHistory = () => {
  const { fetchPayments, payments } = usePaymentHistory();
  const [selectedPayment, setSelectedPayment] = useState<StudentPOStatusShort | null>(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  return (
    <>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Payments
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480, overflowX: "auto" }}>
        <Table stickyHeader size="small" aria-label="Student payments table">
          <TableHead>
            <TableRow>
              {/* <TableCell align="center">ID</TableCell> */}
              <TableCell align="center" sx={cellSx}>Date</TableCell>
              <TableCell align="center" sx={cellSx}>Status</TableCell>
              <TableCell align="center" sx={cellSx}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={cellSx}>
                  <Typography variant="body2" color="text.secondary">
                    No payments found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment.poId} hover>
                  {/* <TableCell align="center">{payment.poId}</TableCell> */}
                  <TableCell align="center" sx={cellSx}>{payment.createdDate?.split(" ")[0]}</TableCell>
                  <TableCell align="center" sx={cellSx}>{payment.status}</TableCell>
                  <TableCell align="center" sx={cellSx}>
                    <Button size="small" sx={{ fontSize: cellSx.fontSize, minWidth: "auto", px: { xs: 1, sm: 1.5 } }} onClick={() => setSelectedPayment(payment)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={selectedPayment !== null} onClose={() => setSelectedPayment(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>Payment Details</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Stack spacing={1.5} mt={1}>
              <PaymentDetailRow label="POID" value={selectedPayment.poId} />
              <PaymentDetailRow label="Created Date" value={selectedPayment.createdDate} />
              <PaymentDetailRow label="Status" value={selectedPayment.status} />
              <PaymentDetailRow label="Amount" value={selectedPayment.amount} />
              {(selectedPayment.pgrRespMessage || selectedPayment.status !== "CHARGED") && (
                <PaymentDetailRow label="Gateway Response Message" value={selectedPayment.pgrRespMessage} />
              )}
              {(selectedPayment.pgrNetworkErrorMessage || selectedPayment.status !== "CHARGED") && (
                <PaymentDetailRow label="Gateway Network Error" value={selectedPayment.pgrNetworkErrorMessage} />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSelectedPayment(null)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const detailTextSx = { fontSize: { xs: "0.75rem", sm: "0.875rem" } };

const PaymentDetailRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary" sx={detailTextSx}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ ...detailTextSx, textAlign: "right" }}>
      {value ?? "-"}
    </Typography>
  </Stack>
);
